import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { naira, compactNaira } from "../../lib/format";
import {
  TrendingUp, Wallet, ChevronsDown, Receipt, BarChart3, Circle, Landmark, FileText,
  ArrowUpRight, ArrowDownRight, MoreHorizontal, RefreshCw, Lock, ChevronRight,
  Building2, CreditCard, ArrowRightLeft, Calendar as CalIcon,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { C } from "./AccountingLayout";
import { useAuth } from "../../context/AuthContext";
import RoleDashboard from "../../components/RoleDashboard";

const CAT_COLOR = { Pizza: "#E53935", Drinks: "#F4B400", Cuisine: "#43A047", Combos: "#3F51B5", Bakery: "#8E24AA", Gelato: "#8E24AA" };
const PALETTE = ["#E53935", "#F4B400", "#43A047", "#3F51B5", "#8E24AA", "#00897B", "#EF6C00"];

function DeltaChip({ kpi }) {
  const { delta_pct, better = "up" } = kpi || {};
  if (delta_pct === null || delta_pct === undefined) {
    return <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color: C.softMuted }}>—</span>;
  }
  const isUp = delta_pct >= 0;
  const good = (better === "up" && isUp) || (better === "down" && !isUp);
  const color = good ? C.green : C.red;
  const Arrow = isUp ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="text-[12px] font-semibold flex items-center gap-1" style={{ color }}>
      <Arrow size={13} strokeWidth={2.5} />
      {Math.abs(delta_pct).toFixed(2)}%
    </span>
  );
}

function Kpi({ label, kpi, Icon, iconColor, iconBg, formatter = compactNaira, extra, testid }) {
  const value = kpi?.value ?? 0;
  const y = kpi?.yesterday;
  return (
    <div data-testid={testid} className="rounded-xl bg-white border p-4 flex flex-col gap-2 hover:shadow-md transition-shadow"
      style={{ borderColor: C.border, boxShadow: "0 1px 2px rgba(59,39,34,0.03)" }}>
      <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: iconBg, color: iconColor }}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <div className="text-[12px] font-medium" style={{ color: C.muted }}>{label}</div>
      <div className="font-brand font-bold text-[26px] leading-tight" style={{ color: C.text }}>{formatter(value)}</div>
      <div className="text-[11px]" style={{ color: C.softMuted }}>Yesterday: {formatter(y ?? 0)}</div>
      <DeltaChip kpi={kpi} />
      {extra && <div className="text-[10.5px] mt-1 px-2 py-1 rounded-md" style={extra.style}>{extra.label}</div>}
    </div>
  );
}

const FULL_VIEW_ROLES = ["General Manager", "Board/MD", "IT"];

export default function AccountingDashboard() {
  const { user } = useAuth();
  if (user && !user.is_admin && !FULL_VIEW_ROLES.includes(user.role_name)) {
    return <RoleDashboard domain="finance" heading="Finance Dashboard" />;
  }
  return <AccountingDashboardFull />;
}

function AccountingDashboardFull() {
  const { locationId, period } = useOutletContext();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = {};
    if (locationId) params.location_id = locationId;
    if (period) params.period = period;
    api.get("/accounting/dashboard", { params }).then(setD).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [locationId, period]);

  if (loading || !d) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {[...Array(8)].map((_, i) => <div key={i} className="h-44 rounded-xl border bg-white animate-pulse" style={{ borderColor: C.border }} />)}
      </div>
    );
  }

  const k = d.kpis;
  const marginPct = d.pl.revenue > 0 ? (d.pl.gross_profit / d.pl.revenue * 100).toFixed(2) : 0;
  const ebitdaOfRev = d.pl.revenue > 0 ? (d.pl.ebitda / d.pl.revenue * 100).toFixed(2) : 0;
  const netOfRev = d.pl.revenue > 0 ? (d.pl.net_profit / d.pl.revenue * 100).toFixed(2) : 0;
  const totalCatRev = (d.top_categories || []).reduce((s, x) => s + x.value, 0);
  const catRows = (d.top_categories || []).slice(0, 6).map((c, i) => ({
    ...c, color: CAT_COLOR[c.name] || PALETTE[i % PALETTE.length],
    share: totalCatRev > 0 ? c.value / totalCatRev * 100 : 0,
  }));
  const cf = d.cash_flow;
  const cfTotal = Math.max(cf.inflow + cf.outflow, 1);
  const cfInPct = (cf.inflow / cfTotal * 100).toFixed(1);
  const cfOutPct = (cf.outflow / cfTotal * 100).toFixed(1);
  const ap = d.aged_payables.buckets;
  const ps = d.period_stats;

  return (
    <div className="space-y-5" data-testid="accounting-dashboard">
      <div className="flex justify-end -mt-2 -mb-1">
        <div className="flex items-center gap-3 text-[11px]" style={{ color: C.softMuted }}>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: C.green }} /> Data updated moments ago</span>
          <button onClick={load} className="flex items-center gap-1.5 font-semibold hover:text-[#3B2722]" data-testid="acc-refresh"><RefreshCw size={11} /> Refresh</button>
        </div>
      </div>

      {/* 8 KPI CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Kpi testid="kpi-total-revenue" label="Total Revenue" kpi={k.total_revenue} Icon={TrendingUp} iconColor="#B4570A" iconBg="#FEF0DA" />
        <Kpi testid="kpi-gross-profit" label="Gross Profit" kpi={k.gross_profit} Icon={Circle} iconColor={C.gold} iconBg="#FEF3D8"
          extra={{ label: `Margin: ${marginPct}%`, style: { background: C.amberTint, color: "#8B5E14", fontWeight: 600 } }} />
        <Kpi testid="kpi-cogs" label="COGS" kpi={k.cogs} Icon={ChevronsDown} iconColor="#B4570A" iconBg="#FEF0DA" />
        <Kpi testid="kpi-op-expenses" label="Operating Expenses" kpi={k.operating_expense} Icon={FileText} iconColor="#1E5FBF" iconBg="#E7F1FE" />
        <Kpi testid="kpi-ebitda" label="EBITDA" kpi={k.ebitda} Icon={BarChart3} iconColor={C.green} iconBg={C.greenTint}
          extra={{ label: `${ebitdaOfRev}% of Revenue`, style: { background: C.greenTint, color: C.green, fontWeight: 600 } }} />
        <Kpi testid="kpi-net-profit" label="Net Profit" kpi={k.net_profit} Icon={Circle} iconColor="#6E28A6" iconBg="#F2E7FD"
          extra={{ label: `${netOfRev}% of Revenue`, style: { background: "#F2E7FD", color: "#6E28A6", fontWeight: 600 } }} />
        <Kpi testid="kpi-cash-balance" label="Cash Balance" kpi={k.cash_balance} Icon={Landmark} iconColor="#00897B" iconBg="#DBEEEB" />
        <Kpi testid="kpi-ap" label="Accounts Payable" kpi={k.accounts_payable} Icon={Receipt} iconColor="#D63384" iconBg="#FCE4EF"
          extra={d.aged_payables.overdue_count > 0 ? { label: `${d.aged_payables.overdue_count} Overdue`, style: { background: C.redTint, color: C.red, fontWeight: 600 } } : null} />
      </div>

      {/* Row 2: P&L bar chart, Cash Flow donut, Aged AP */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_320px] gap-4">
        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="pl-chart">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-brand font-bold text-lg" style={{ color: C.text }}>Profit & Loss <span className="text-[13px] font-normal" style={{ color: C.softMuted }}>(This Month)</span></h3>
            </div>
            <div className="h-8 px-3 rounded-md border flex items-center gap-2 text-[12px] font-semibold" style={{ borderColor: C.border, color: C.text }}>This Month <MoreHorizontal size={12} /></div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.weekly_pl} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1E7D3" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.softMuted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: C.softMuted }} axisLine={false} tickLine={false} width={55}
                tickFormatter={(v) => v >= 1e6 ? `₦${(v / 1e6).toFixed(0)}M` : v >= 1e3 ? `₦${(v / 1e3).toFixed(0)}K` : `₦${v}`} />
              <Tooltip formatter={(v) => naira(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
              <Bar dataKey="revenue" fill={C.burgundy} name="Revenue" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cogs" fill={C.gold} name="COGS" radius={[3, 3, 0, 0]} />
              <Bar dataKey="gross_profit" fill={C.green} name="Gross Profit" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" fill="#1E5FBF" name="Expenses" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2 text-[11px] font-semibold" style={{ color: C.muted }}>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.burgundy }} /> Revenue</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.gold }} /> COGS</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.green }} /> Gross Profit</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#1E5FBF" }} /> Expenses</span>
          </div>
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="cash-flow-summary">
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Cash Flow Summary <span className="text-[13px] font-normal" style={{ color: C.softMuted }}>(This Month)</span></h3>
          <div className="grid grid-cols-2 gap-4 items-center">
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-1.5 text-[12px]" style={{ color: C.muted }}><span className="w-2 h-2 rounded-full" style={{ background: C.green }} /> Cash Inflow</div>
                <div className="font-brand font-bold text-xl mt-0.5" style={{ color: C.text }}>{compactNaira(cf.inflow)}</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-[12px]" style={{ color: C.muted }}><span className="w-2 h-2 rounded-full" style={{ background: C.red }} /> Cash Outflow</div>
                <div className="font-brand font-bold text-xl mt-0.5" style={{ color: C.text }}>{compactNaira(cf.outflow)}</div>
              </div>
              <div className="pt-2 border-t" style={{ borderColor: C.borderSoft }}>
                <div className="text-[11px] tracking-widest font-bold" style={{ color: C.softMuted }}>NET CASH FLOW</div>
                <div className="font-brand font-bold text-xl mt-0.5" style={{ color: cf.net >= 0 ? C.green : C.red }}>{compactNaira(cf.net)}</div>
              </div>
            </div>
            <div className="relative h-[170px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[{ name: "Inflow", value: cf.inflow }, { name: "Outflow", value: cf.outflow }]}
                    dataKey="value" innerRadius={48} outerRadius={72} stroke="none">
                    <Cell fill={C.green} /><Cell fill={C.red} />
                  </Pie>
                  <Tooltip formatter={(v) => naira(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${C.border}` }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-brand font-bold text-lg" style={{ color: C.text }}>{compactNaira(cf.net)}</div>
                <div className="text-[9px] font-semibold tracking-wider uppercase" style={{ color: C.softMuted }}>Net Cash Flow</div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-3 text-[11px] font-semibold" style={{ color: C.muted }}>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: C.green }} /> Inflow {cfInPct}%</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: C.red }} /> Outflow {cfOutPct}%</span>
          </div>
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="aged-ap">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-brand font-bold text-lg" style={{ color: C.text }}>Aged Accounts Payable</h3>
            <button className="text-[13px] font-semibold" style={{ color: C.gold }} data-testid="view-all-ap">View all</button>
          </div>
          <div className="space-y-3">
            {[
              { label: "Current (0-30 days)", val: ap.current, color: C.text },
              { label: "Due (31-60 days)", val: ap.due_30_60, color: C.text },
              { label: "Overdue (61-90 days)", val: ap.overdue_61_90, color: C.red },
              { label: "Overdue (90+ days)", val: ap.overdue_90p, color: C.red },
            ].map((r, i) => (
              <div key={i} className="flex items-center justify-between text-[13px]">
                <span style={{ color: C.muted }}>{r.label}</span>
                <span className="font-brand font-bold tabular-nums" style={{ color: r.color }}>{compactNaira(r.val)}</span>
              </div>
            ))}
            <div className="pt-2 border-t flex items-center justify-between" style={{ borderColor: C.borderSoft }}>
              <span className="text-[13px] font-semibold" style={{ color: C.text }}>Total</span>
              <span className="font-brand font-bold text-lg tabular-nums" style={{ color: C.red }}>{compactNaira(ap.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Recent Journal Entries, Budget vs Actual */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="recent-journals">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-brand font-bold text-lg" style={{ color: C.text }}>Recent Journal Entries</h3>
            <button className="text-[13px] font-semibold" style={{ color: C.gold }} data-testid="view-all-journals">View all</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest" style={{ color: C.softMuted }}>
                  <th className="pb-3 pr-3">Date</th><th className="pb-3 pr-3">Journal ID</th><th className="pb-3 pr-3">Description</th>
                  <th className="pb-3 pr-3">Source</th><th className="pb-3 pr-3 text-right">Debit (₦)</th>
                  <th className="pb-3 pr-3 text-right">Credit (₦)</th><th className="pb-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {(d.recent_journals || []).map((j, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: C.borderSoft }}>
                    <td className="py-2.5 pr-3 text-[12px]" style={{ color: C.muted }}>{(j.date || "").slice(0, 10)}</td>
                    <td className="py-2.5 pr-3 font-semibold text-[12.5px]" style={{ color: C.text }}>{j.number}</td>
                    <td className="py-2.5 pr-3 text-[12.5px]" style={{ color: C.text }}>{j.memo}</td>
                    <td className="py-2.5 pr-3 text-[11px] uppercase font-semibold" style={{ color: C.softMuted }}>{j.source}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{Number(j.total_debit).toLocaleString()}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{Number(j.total_credit).toLocaleString()}</td>
                    <td className="py-2.5"><span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: C.greenTint, color: C.green }}>Posted</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="budget-vs-actual">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-brand font-bold text-lg" style={{ color: C.text }}>Budget vs Actual <span className="text-[13px] font-normal" style={{ color: C.softMuted }}>(This Month)</span></h3>
            <div className="h-8 px-3 rounded-md border flex items-center gap-2 text-[12px] font-semibold" style={{ borderColor: C.border, color: C.text }}>This Month <MoreHorizontal size={12} /></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest" style={{ color: C.softMuted }}>
                  <th className="pb-3 pr-3">Account</th><th className="pb-3 pr-3 text-right">Budget (₦)</th>
                  <th className="pb-3 pr-3 text-right">Actual (₦)</th><th className="pb-3 pr-3 text-right">Variance (₦)</th>
                  <th className="pb-3 text-right">Variance %</th>
                </tr>
              </thead>
              <tbody>
                {(d.budget_variance.rows || []).length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-[12px]" style={{ color: C.softMuted }}>No budgets set for this month yet.</td></tr>
                ) : d.budget_variance.rows.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: C.borderSoft }}>
                    <td className="py-2.5 pr-3 text-[12.5px]" style={{ color: C.text }}>{r.account_name}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums" style={{ color: C.text }}>{compactNaira(r.budget)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{compactNaira(r.actual)}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums" style={{ color: r.variance >= 0 ? C.green : C.red }}>{compactNaira(Math.abs(r.variance))}</td>
                    <td className="py-2.5 text-right font-semibold" style={{ color: r.variance_pct >= 0 ? C.green : C.red }}>{r.variance_pct.toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Row 4: Top Revenue Cat, Unreconciled, VAT, Period Status */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="top-revenue-cat">
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Top Revenue by Category <span className="text-[13px] font-normal" style={{ color: C.softMuted }}>(This Month)</span></h3>
          {catRows.length === 0 ? <div className="text-sm py-6 text-center" style={{ color: C.softMuted }}>No sales this month.</div> : (
            <div className="flex items-center gap-3">
              <div className="w-[130px] h-[130px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={catRows} dataKey="value" innerRadius={38} outerRadius={62} stroke="none">
                      {catRows.map((c, i) => <Cell key={i} fill={c.color} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5 text-[12px]">
                {catRows.map((c, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5" style={{ color: C.text }}><span className="w-2 h-2 rounded-full" style={{ background: c.color }} />{c.name}</span>
                    <span className="tabular-nums" style={{ color: C.softMuted }}>{compactNaira(c.value)} ({c.share.toFixed(1)}%)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="unreconciled">
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Unreconciled Items</h3>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[
              { label: "Bank Transactions", Icon: Building2, count: d.unreconciled.bank.count, amt: d.unreconciled.bank.amount, tint: "#FEF3D8", color: "#B45309" },
              { label: "Card Settlements", Icon: CreditCard, count: d.unreconciled.card.count, amt: d.unreconciled.card.amount, tint: "#E7F1FE", color: "#1E5FBF" },
              { label: "Transfers", Icon: ArrowRightLeft, count: d.unreconciled.transfer.count, amt: d.unreconciled.transfer.amount, tint: "#F2E7FD", color: "#6E28A6" },
            ].map((r, i) => (
              <div key={i} className="rounded-lg border p-2.5 text-center" style={{ borderColor: C.borderSoft }}>
                <div className="w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-1.5" style={{ background: r.tint, color: r.color }}>
                  <r.Icon size={14} />
                </div>
                <div className="text-[10px] font-semibold" style={{ color: C.softMuted }}>{r.label}</div>
                <div className="font-brand font-bold text-xl mt-0.5" style={{ color: C.text }}>{r.count}</div>
                <div className="text-[10.5px] tabular-nums" style={{ color: C.softMuted }}>{compactNaira(r.amt)}</div>
              </div>
            ))}
          </div>
          <button className="w-full flex items-center justify-center gap-1.5 text-[12.5px] font-semibold py-2 border-t" style={{ borderColor: C.borderSoft, color: C.gold }}>
            View Reconciliation <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="vat-summary">
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Tax (VAT) Summary</h3>
          <div className="space-y-2.5 text-[13px]">
            <div className="flex justify-between"><span style={{ color: C.muted }}>Taxable Sales</span><span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>{compactNaira(d.vat_summary.taxable_sales)}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>VAT Collected (7.5%)</span><span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>{compactNaira(d.vat_summary.vat_collected)}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>VAT Paid</span><span className="font-brand font-bold tabular-nums" style={{ color: C.text }}>{compactNaira(d.vat_summary.vat_paid)}</span></div>
            <div className="flex justify-between border-t pt-2" style={{ borderColor: C.borderSoft }}>
              <span className="font-semibold" style={{ color: C.text }}>VAT Payable</span>
              <span className="font-brand font-bold tabular-nums" style={{ color: C.red }}>{compactNaira(d.vat_summary.vat_payable)}</span>
            </div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>Return Period</span><span className="font-semibold" style={{ color: C.text }}>{new Date(period + "-01").toLocaleDateString("en-NG", { month: "short", year: "numeric" })}</span></div>
          </div>
          <button className="w-full flex items-center justify-center gap-1.5 text-[12.5px] font-semibold py-2 mt-3 border-t" style={{ borderColor: C.borderSoft, color: C.gold }}>
            View VAT Report <ChevronRight size={13} strokeWidth={2.5} />
          </button>
        </div>

        <div className="rounded-xl bg-white border p-5" style={{ borderColor: C.border }} data-testid="period-status">
          <h3 className="font-brand font-bold text-lg mb-3" style={{ color: C.text }}>Period Status</h3>
          <div className="rounded-lg p-3 border" style={{ borderColor: C.borderSoft, background: "#FDFBF5" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalIcon size={15} style={{ color: C.gold }} />
                <span className="font-brand font-bold" style={{ color: C.text }}>{new Date(period + "-01").toLocaleDateString("en-NG", { month: "long", year: "numeric" })}</span>
              </div>
              <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: d.period_status === "open" ? C.greenTint : C.redTint, color: d.period_status === "open" ? C.green : C.red }}>{d.period_status === "open" ? "Open" : "Closed"}</span>
            </div>
            <div className="text-[11px] mt-1" style={{ color: C.softMuted }}>01 – 30 {new Date(period + "-01").toLocaleDateString("en-NG", { month: "short", year: "numeric" })}</div>
          </div>
          <div className="mt-3 space-y-2 text-[13px]">
            <div className="flex justify-between"><span style={{ color: C.muted }}>Journals Posted</span><span className="font-brand font-bold" style={{ color: C.text }}>{ps.journals_posted}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>Pending Journals</span><span className="font-brand font-bold" style={{ color: C.text }}>{ps.pending_journals}</span></div>
            <div className="flex justify-between"><span style={{ color: C.muted }}>Unposted Events</span><span className="font-brand font-bold" style={{ color: ps.unposted_events > 0 ? C.red : C.text }}>{ps.unposted_events}</span></div>
          </div>
          <button className="w-full flex items-center justify-center gap-1.5 text-[12.5px] font-semibold py-2 mt-4 rounded-md text-white" style={{ background: C.burgundy }} data-testid="close-period-btn">
            <Lock size={13} /> Close Period
          </button>
        </div>
      </div>
    </div>
  );
}
